// Debug-only helper for inspecting specs history diffs in the Specs History tab.
type DiffOperation = "add" | "remove" | "replace";

export interface SpecsRevisionDiffEntry {
  op: DiffOperation;
  path: string;
  old?: unknown;
  new?: unknown;
}

function isPrimitiveValue(
  value: unknown
): value is string | number | boolean | null | undefined {
  return (
    value == null || ["string", "number", "boolean"].includes(typeof value)
  );
}

function hasOnlyUniqueValues(
  values: Array<string | number | boolean | null | undefined>
) {
  const seen = new Set<string>();

  for (const value of values) {
    const key = `${typeof value}:${String(value)}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
  }

  return true;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

function formatPathSegment(path: string, segment: string | number) {
  if (typeof segment === "number") {
    return `${path}[${segment}]`;
  }

  return path ? `${path}.${segment}` : segment;
}

function pushAddedEntries(
  value: unknown,
  path: string,
  entries: SpecsRevisionDiffEntry[]
) {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      pushAddedEntries(item, formatPathSegment(path, index), entries);
    });
    return;
  }

  if (isPlainObject(value)) {
    Object.entries(value).forEach(([key, item]) => {
      pushAddedEntries(item, formatPathSegment(path, key), entries);
    });
    return;
  }

  entries.push({ op: "add", path, new: value });
}

function pushRemovedEntries(
  value: unknown,
  path: string,
  entries: SpecsRevisionDiffEntry[]
) {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      pushRemovedEntries(item, formatPathSegment(path, index), entries);
    });
    return;
  }

  if (isPlainObject(value)) {
    Object.entries(value).forEach(([key, item]) => {
      pushRemovedEntries(item, formatPathSegment(path, key), entries);
    });
    return;
  }

  entries.push({ op: "remove", path, old: value });
}

function diffPrimitiveArrays(
  previousValue: Array<string | number | boolean | null | undefined>,
  nextValue: Array<string | number | boolean | null | undefined>,
  path: string,
  entries: SpecsRevisionDiffEntry[]
) {
  if (hasOnlyUniqueValues(previousValue) && hasOnlyUniqueValues(nextValue)) {
    const nextValueSet = new Set(
      nextValue.map((value) => `${typeof value}:${String(value)}`)
    );
    previousValue.forEach((value, index) => {
      if (!nextValueSet.has(`${typeof value}:${String(value)}`)) {
        entries.push({
          op: "remove",
          path: formatPathSegment(path, index),
          old: value,
        });
      }
    });

    const previousValueSet = new Set(
      previousValue.map((value) => `${typeof value}:${String(value)}`)
    );
    nextValue.forEach((value, index) => {
      if (!previousValueSet.has(`${typeof value}:${String(value)}`)) {
        entries.push({
          op: "add",
          path: formatPathSegment(path, index),
          new: value,
        });
      }
    });
    return;
  }

  const lcsLengths = Array.from({ length: previousValue.length + 1 }, () =>
    Array<number>(nextValue.length + 1).fill(0)
  );

  for (
    let previousIndex = previousValue.length - 1;
    previousIndex >= 0;
    previousIndex -= 1
  ) {
    for (let nextIndex = nextValue.length - 1; nextIndex >= 0; nextIndex -= 1) {
      if (Object.is(previousValue[previousIndex], nextValue[nextIndex])) {
        lcsLengths[previousIndex][nextIndex] =
          lcsLengths[previousIndex + 1][nextIndex + 1] + 1;
      } else {
        lcsLengths[previousIndex][nextIndex] = Math.max(
          lcsLengths[previousIndex + 1][nextIndex],
          lcsLengths[previousIndex][nextIndex + 1]
        );
      }
    }
  }

  let previousIndex = 0;
  let nextIndex = 0;

  while (previousIndex < previousValue.length || nextIndex < nextValue.length) {
    if (
      previousIndex < previousValue.length &&
      nextIndex < nextValue.length &&
      Object.is(previousValue[previousIndex], nextValue[nextIndex])
    ) {
      previousIndex += 1;
      nextIndex += 1;
      continue;
    }

    const canRemove = previousIndex < previousValue.length;
    const canAdd = nextIndex < nextValue.length;
    const removeScore = canRemove
      ? lcsLengths[previousIndex + 1][nextIndex]
      : Number.NEGATIVE_INFINITY;
    const addScore = canAdd
      ? lcsLengths[previousIndex][nextIndex + 1]
      : Number.NEGATIVE_INFINITY;

    if (canRemove && (!canAdd || removeScore >= addScore)) {
      entries.push({
        op: "remove",
        path: formatPathSegment(path, previousIndex),
        old: previousValue[previousIndex],
      });
      previousIndex += 1;
      continue;
    }

    entries.push({
      op: "add",
      path: formatPathSegment(path, nextIndex),
      new: nextValue[nextIndex],
    });
    nextIndex += 1;
  }
}

function diffValues(
  previousValue: unknown,
  nextValue: unknown,
  path: string,
  entries: SpecsRevisionDiffEntry[]
) {
  if (Object.is(previousValue, nextValue)) {
    return;
  }

  if (previousValue === undefined) {
    pushAddedEntries(nextValue, path, entries);
    return;
  }

  if (nextValue === undefined) {
    pushRemovedEntries(previousValue, path, entries);
    return;
  }

  if (Array.isArray(previousValue) && Array.isArray(nextValue)) {
    if (
      previousValue.every(isPrimitiveValue) &&
      nextValue.every(isPrimitiveValue)
    ) {
      diffPrimitiveArrays(previousValue, nextValue, path, entries);
      return;
    }

    const maxLength = Math.max(previousValue.length, nextValue.length);
    for (let index = 0; index < maxLength; index += 1) {
      diffValues(
        previousValue[index],
        nextValue[index],
        formatPathSegment(path, index),
        entries
      );
    }
    return;
  }

  if (isPlainObject(previousValue) && isPlainObject(nextValue)) {
    const keys = new Set([
      ...Object.keys(previousValue),
      ...Object.keys(nextValue),
    ]);
    Array.from(keys)
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => {
        diffValues(
          previousValue[key],
          nextValue[key],
          formatPathSegment(path, key),
          entries
        );
      });
    return;
  }

  entries.push({
    op: "replace",
    path,
    old: previousValue,
    new: nextValue,
  });
}

export function diffSpecsSnapshots(
  previousSnapshot: Record<string, unknown>,
  nextSnapshot: Record<string, unknown>
) {
  const entries: SpecsRevisionDiffEntry[] = [];
  diffValues(previousSnapshot, nextSnapshot, "specs", entries);
  return entries;
}

export function formatSpecsRevisionDiff(
  previousSnapshot: Record<string, unknown>,
  nextSnapshot: Record<string, unknown>
) {
  const entries = diffSpecsSnapshots(previousSnapshot, nextSnapshot);
  if (entries.length === 0) {
    return "specs identical";
  }

  return JSON.stringify(entries, null, 2);
}
